import React from "react";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { useIdentityAttachmentPreview } from "@/screen/Identities/hooks/useIdentityAttachmentPreview";
import type { IdentityAttachmentPreviewProps } from "@/screen/Identities/components/Interfaces/IdentityAttachmentPreviewInterfaces";
import { AttachmentSpinnerWrapper, AttachmentImage, AttachmentLink, AttachmentPdfWrapper, AttachmentPdfFrame } from "@/screen/Identities/components/Styles/IdentityAttachmentPreviewStyles";

const IdentityAttachmentPreview: React.FC<IdentityAttachmentPreviewProps> = ({source, alt, placeholder, className, onResolvedFile, onErrorChange, onLoadingChange}) =>
{
    const {status, objectUrl, mimeType, resolvedFile, error} = useIdentityAttachmentPreview(source, alt);

    const onResolvedFileRef = React.useRef(onResolvedFile);
    onResolvedFileRef.current = onResolvedFile;

    React.useEffect(() =>
    {
        if(resolvedFile && onResolvedFileRef.current)
        {
            onResolvedFileRef.current(resolvedFile);
        }
    }, [resolvedFile]);

    const onErrorChangeRef = React.useRef(onErrorChange);
    onErrorChangeRef.current = onErrorChange;

    React.useEffect(() =>
    {
        if(status === "error")
        {
            onErrorChangeRef.current?.(error || "Error al recuperar el adjunto.");
            return;
        }
        onErrorChangeRef.current?.(null);
    }, [status, error]);

    const onLoadingChangeRef = React.useRef(onLoadingChange);
    onLoadingChangeRef.current = onLoadingChange;

    React.useEffect(() =>
    {
        onLoadingChangeRef.current?.(status === "loading");
    }, [status]);

    if(status === "idle")
    {
        return placeholder ? <>{placeholder}</> : null;
    }

    if(status === "loading")
    {
        return(
            <AttachmentSpinnerWrapper>
                <Spinner size={SpinnerSize.medium} label="Cargando adjunto..." />
            </AttachmentSpinnerWrapper>
        );
    }

    if(status === "error" || !objectUrl)
    {
        return placeholder ? <>{placeholder}</> : null;
    }

    // blob: URLs de archivos locales recién seleccionados se tratan como imagen (comportamiento del browser)
    const isBlobWithUnknownMime = objectUrl.startsWith("blob:") && !mimeType;
    const isImage = (mimeType?.startsWith("image/") ?? false) || isBlobWithUnknownMime;
    const isPdf = mimeType === "application/pdf";

    if(isImage)
    {
        return <AttachmentImage src={objectUrl} alt={alt} className={className} />;
    }

    if(isPdf)
    {
        return(
            <AttachmentLink href={objectUrl} target="_blank" rel="noreferrer" download>
                <AttachmentPdfWrapper>
                    <AttachmentPdfFrame src={objectUrl} title={alt} />
                </AttachmentPdfWrapper>
            </AttachmentLink>
        );
    }

    return(
        <AttachmentLink href={objectUrl} target="_blank" rel="noreferrer" download>
            {alt}
        </AttachmentLink>
    );
};

export default IdentityAttachmentPreview;